import React, { useState, useEffect, useCallback } from "react";
import { Plus, Check, X, User, Calendar, Loader2, RefreshCw } from "lucide-react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useNavigate } from "react-router-dom";
import CommonAPIs from "../../services/commonAPIs";
import { AppUtils } from "../../services/AppUtils";
import PageLayout from "../../components/PageLayout";
import useAnalysis from "../../services/useAnalysis";
import { StatCard } from "./StatCard";
import { MemberCard } from "./MemberCard";
import { SearchModal } from "./SearchModal";
import "./MyCoreTeam.css";

const SwalAlert = withReactContent(Swal);

// Transform API data to component format
function transformApiDataToComponentFormat(apiData, ranks) {
  if (!apiData || !Array.isArray(apiData)) return [];

  return apiData
    .map((member) => {
      // Validate required fields
      if (!member.distributor_id) {
        console.warn("Member missing distributor_id:", member);
        return null;
      }

      return {
        id: member.distributor_id.toString(),
        name: member.distributor_name?.trim() || "Unknown",
        phone: member.phone || "N/A",
        joinDate: member.join_time,
        currentRank: member.rank || "N/A",
        nextRank: AppUtils.getNextRank(member.rank_id, ranks),
        rankImage: AppUtils.getRankImage(member.rank_id, ranks),
        status: member.mactivity_status_title,
        personalPV: member.personal_pv || 0,
        teamSize: member.team_size || 0,
        leftVolume: member.lgv || 0,
        rightVolume: member.rgv || 0,
        championsLeagueQualified: member.is_champions_league_eligible === 1,

        // Transform income trend data
        weeklyIncome:
          member.incomeTrend
            ?.map((week) => ({
              week: AppUtils.formatWeekId(week.week_id),
              income: week.income || 0,
            }))
            .reverse() || [], // Reverse to show oldest first

        // Transform volume trend data
        volumeData:
          member.volumeTrend
            ?.map((week) => ({
              week: AppUtils.formatWeekId(week.week_id),
              leftVolume: week.lgv || 0,
              rightVolume: week.rgv || 0,
            }))
            .reverse() || [], // Reverse to show oldest first
      };
    })
    .filter(Boolean); // Remove null entries
}

const MyCoreTeam = () => {
  const {
    getMyCoreTeamDashboard,
    getMyCoreTeamDashboardSummary,
    getOutgoingRequests,
    getIncomingRequests,
    sendCoreTeamRequest,
    removeCoreTeamMember,
    declineRequestCoreTeamMember,
    acceptRequestCoreTeamMember,
    cancelRequestCoreTeamMember,
  } = useAnalysis();
  const { fetchDistDetails } = CommonAPIs();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [teamData, setTeamData] = useState({
    summary: {
      totalMembers: 0,
      maxMembers: 10,
      activeMembers: 0,
      pendingRequests: 0,
      totalTeamVolume: 0,
      averageRank: "N/A",
    },
    members: [],
    pendingRequests: [],
    incomingRequests: [],
    outgoingRequests: [],
  });
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiErrors, setApiErrors] = useState({
    summary: false,
    members: false,
    incomingRequests: false,
    outgoingRequests: false,
  });
  const [operationLoading, setOperationLoading] = useState({
    remove: false,
    accept: false,
    decline: false,
    add: false,
  });

  const distributor = JSON.parse(localStorage.getItem("distributor") || "{}");
  const ranks = JSON.parse(localStorage.getItem("ranks") || "[]");

  // Calculate additional summary data from transformed members
  const calculateAdditionalSummary = useCallback(
    (members, summaryFromAPI) => {
      const totalTeamVolume = members.reduce((sum, m) => sum + m.leftVolume + m.rightVolume, 0);
      const averageRankId =
        members.length > 0
          ? Math.round(
              members.reduce((sum, m) => sum + (ranks.find((r) => r.title === m.currentRank)?.id || 1), 0) /
                members.length
            )
          : 1;
      const averageRank = ranks.find((r) => r.id === averageRankId)?.title || "N/A";

      return {
        ...summaryFromAPI,
        totalTeamVolume,
        averageRank,
      };
    },
    [ranks]
  );

  // Separate functions for loading data - using distributor_id directly to avoid recreation
  const loadSummaryData = useCallback(() => {
    const distributorId = distributor.distributor_id;
    return new Promise((resolve, reject) => {
      const makeRequest = (retryCount = 0) => {
        getMyCoreTeamDashboardSummary(
          { distributor_id: distributorId },
          (data) => {
            console.log("Core team summary data received:", data);
            const summaryData = data?.[0] || {
              totalMembers: 0,
              maxMembers: 10,
              activeMembers: 0,
              pendingRequests: 0,
            };
            resolve(summaryData);
          },
          (error) => {
            console.error("Summary API Error:", error);
            if (retryCount < 2) {
              console.log(`Retrying summary API (attempt ${retryCount + 1})`);
              setTimeout(() => makeRequest(retryCount + 1), 1000 * (retryCount + 1));
            } else {
              reject(error);
            }
          }
        );
      };
      makeRequest();
    });
  }, []);

  const loadMembersData = useCallback(() => {
    const distributorId = distributor.distributor_id;
    return new Promise((resolve, reject) => {
      getMyCoreTeamDashboard(
        { distributor_id: distributorId },
        (data) => {
          console.log("Core team data received:", data);
          resolve(data);
        },
        (error) => {
          console.error("Members API Error:", error);
          reject(error);
        }
      );
    });
  }, []);

  const loadIncomingRequests = useCallback(() => {
    const distributorId = distributor.distributor_id;
    return new Promise((resolve, reject) => {
      getIncomingRequests(
        { distributor_id: distributorId },
        (data) => {
          console.log("Incoming requests data received:", data);
          resolve(data || []);
        },
        (error) => {
          console.error("Incoming requests API Error:", error);
          reject(error);
        }
      );
    });
  }, []);

  const loadOutgoingRequests = useCallback(() => {
    const distributorId = distributor.distributor_id;
    return new Promise((resolve, reject) => {
      getOutgoingRequests(
        { distributor_id: distributorId },
        (data) => {
          console.log("Outgoing requests data received:", data);
          resolve(data || []);
        },
        (error) => {
          console.error("Outgoing requests API Error:", error);
          reject(error);
        }
      );
    });
  }, []);

  useEffect(() => {
    if (!distributor?.distributor_id) {
      setError("Distributor information not found");
      setLoading(false);
      return;
    }

    // Check network connectivity
    if (!navigator.onLine) {
      setError("No internet connection. Please check your network and try again.");
      setLoading(false);
      return;
    }

    // Reset API errors
    setApiErrors({
      summary: false,
      members: false,
      incomingRequests: false,
      outgoingRequests: false,
    });

    // Load all data independently to handle partial failures
    const loadData = async () => {
      let summaryData = null;
      let membersData = null;
      let incomingRequestsData = [];
      let outgoingRequestsData = [];

      // Load summary data
      try {
        summaryData = await loadSummaryData();
      } catch (error) {
        console.error("Failed to load summary data:", error);
        setApiErrors((prev) => ({ ...prev, summary: true }));
        summaryData = {
          totalMembers: 0,
          maxMembers: 10,
          activeMembers: 0,
          pendingRequests: 0,
        };
      }

      // Load members data
      try {
        membersData = await loadMembersData();
      } catch (error) {
        console.error("Failed to load members data:", error);
        setApiErrors((prev) => ({ ...prev, members: true }));
        membersData = [];
      }

      // Load incoming requests
      try {
        incomingRequestsData = await loadIncomingRequests();
      } catch (error) {
        console.error("Failed to load incoming requests:", error);
        setApiErrors((prev) => ({ ...prev, incomingRequests: true }));
        incomingRequestsData = [];
      }

      // Load outgoing requests
      try {
        outgoingRequestsData = await loadOutgoingRequests();
      } catch (error) {
        console.error("Failed to load outgoing requests:", error);
        setApiErrors((prev) => ({ ...prev, outgoingRequests: true }));
        outgoingRequestsData = [];
      }

      try {
        const transformedMembers = transformApiDataToComponentFormat(membersData, ranks);
        console.log("Transformed members:", transformedMembers);
        const summary = calculateAdditionalSummary(transformedMembers, summaryData);

        setTeamData({
          summary,
          members: transformedMembers,
          pendingRequests: [], // This would come from a separate API
          incomingRequests: incomingRequestsData,
          outgoingRequests: outgoingRequestsData,
        });
        setError(null);
      } catch (err) {
        console.error("Error transforming data:", err);
        setError("Failed to process team data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []); // Empty dependency array - only run once on mount

  const handleRemoveMember = (memberId) => {
    SwalAlert.fire({
      title: "Remove Member",
      text: "Are you sure you want to remove this member from your core team?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Remove",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setOperationLoading((prev) => ({ ...prev, remove: true }));
        const payload = {
          sender_distributor_id: distributor.distributor_id,
          receiver_distributor_id: memberId,
        };

        removeCoreTeamMember(
          payload,
          (response) => {
            console.log("Member removed successfully:", response);
            SwalAlert.fire({
              title: "Success!",
              text: "Member removed from core team successfully!",
              icon: "success",
              button: "OK",
            });

            // Refresh summary data and members
            Promise.all([loadSummaryData(), loadMembersData()])
              .then(([summaryData, membersData]) => {
                const transformedMembers = transformApiDataToComponentFormat(membersData, ranks);
                const summary = calculateAdditionalSummary(transformedMembers, summaryData);

                setTeamData((prev) => ({
                  ...prev,
                  summary,
                  members: transformedMembers,
                }));
              })
              .catch((error) => {
                console.error("Failed to refresh data after removing member:", error);
              });
            setOperationLoading((prev) => ({ ...prev, remove: false }));
          },
          (error) => {
            console.error("Failed to remove member:", error);
            SwalAlert.fire({
              title: "Error!",
              text: "Failed to remove member. Please try again.",
              icon: "error",
              button: "OK",
            });
            setOperationLoading((prev) => ({ ...prev, remove: false }));
          }
        );
      }
    });
  };

  const handleViewDashboard = (memberId) => {
    // Navigate to read-only dashboard with distributor ID in state
    fetchDistDetails(
      { distributor_id: memberId },
      (data) => {
        console.log("Distributor details:", data);
        navigate("/viewanalysisdashboardreadonly", { state: { selecteDist: data } });
      },
      (error) => {
        console.error("Error fetching distributor details:", error);
      }
    );
  };

  const handleAddMember = (memberId) => {
    const payload = {
      sender_distributor_id: distributor.distributor_id,
      receiver_distributor_id: memberId,
      status: "Pending",
    };

    sendCoreTeamRequest(
      payload,
      (response) => {
        console.log("Request sent successfully:", response);
        // Show success message using SweetAlert
        SwalAlert.fire({
          title: "Success!",
          text: "Request sent successfully!",
          icon: "success",
          button: "OK",
        });
        setShowSearchModal(false);

        // Refresh summary data and outgoing requests
        Promise.all([loadSummaryData(), loadOutgoingRequests()])
          .then(([summaryData, outgoingRequestsData]) => {
            setTeamData((prev) => ({
              ...prev,
              summary: calculateAdditionalSummary(prev.members, summaryData),
              outgoingRequests: outgoingRequestsData,
            }));
          })
          .catch((error) => {
            console.error("Failed to refresh data after sending request:", error);
          });
      },
      (error) => {
        console.error("Failed to send request:", error);
        SwalAlert.fire({
          title: "Error!",
          text: "Failed to send request. Please try again.",
          icon: "error",
          button: "OK",
        });
      }
    );
  };

  const handleAcceptRequest = (requestId) => {
    SwalAlert.fire({
      title: "Accept Request",
      text: "Are you sure you want to accept this request?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Accept",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          sender_distributor_id: requestId,
          receiver_distributor_id: distributor.distributor_id,
        };

        acceptRequestCoreTeamMember(
          payload,
          (response) => {
            console.log("Request accepted successfully:", response);
            SwalAlert.fire({
              title: "Success!",
              text: "Request accepted successfully!",
              icon: "success",
              button: "OK",
            });

            // Refresh summary data, members, and incoming requests
            Promise.all([loadSummaryData(), loadMembersData(), loadIncomingRequests()])
              .then(([summaryData, membersData, incomingRequestsData]) => {
                const transformedMembers = transformApiDataToComponentFormat(membersData, ranks);
                const summary = calculateAdditionalSummary(transformedMembers, summaryData);

                setTeamData((prev) => ({
                  ...prev,
                  summary,
                  members: transformedMembers,
                  incomingRequests: incomingRequestsData,
                }));
              })
              .catch((error) => {
                console.error("Failed to refresh data after accepting request:", error);
              });
          },
          (error) => {
            console.error("Failed to accept request:", error);
            SwalAlert.fire({
              title: "Error!",
              text: "Failed to accept request. Please try again.",
              icon: "error",
              button: "OK",
            });
          }
        );
      }
    });
  };

  const handleDeclineRequest = (requestId) => {
    SwalAlert.fire({
      title: "Decline Request",
      text: "Are you sure you want to decline this request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Decline",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          sender_distributor_id: requestId,
          receiver_distributor_id: distributor.distributor_id,
        };

        declineRequestCoreTeamMember(
          payload,
          (response) => {
            console.log("Request declined successfully:", response);
            SwalAlert.fire({
              title: "Success!",
              text: "Request declined successfully!",
              icon: "success",
              button: "OK",
            });

            // Refresh summary data and incoming requests
            Promise.all([loadSummaryData(), loadIncomingRequests()])
              .then(([summaryData, incomingRequestsData]) => {
                setTeamData((prev) => ({
                  ...prev,
                  summary: calculateAdditionalSummary(prev.members, summaryData),
                  incomingRequests: incomingRequestsData,
                }));
              })
              .catch((error) => {
                console.error("Failed to refresh data after declining request:", error);
              });
          },
          (error) => {
            console.error("Failed to decline request:", error);
            SwalAlert.fire({
              title: "Error!",
              text: "Failed to decline request. Please try again.",
              icon: "error",
              button: "OK",
            });
          }
        );
      }
    });
  };

  const handleCancelRequest = (receiverId) => {
    SwalAlert.fire({
      title: "Cancel Request",
      text: "Are you sure you want to cancel this request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel",
      cancelButtonText: "No, Keep",
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          sender_distributor_id: distributor.distributor_id,
          receiver_distributor_id: receiverId,
        };

        cancelRequestCoreTeamMember(
          payload,
          (response) => {
            console.log("Request cancelled successfully:", response);
            SwalAlert.fire({
              title: "Success!",
              text: "Request cancelled successfully!",
              icon: "success",
              button: "OK",
            });

            // Refresh summary data and outgoing requests
            Promise.all([loadSummaryData(), loadOutgoingRequests()])
              .then(([summaryData, outgoingRequestsData]) => {
                setTeamData((prev) => ({
                  ...prev,
                  summary: calculateAdditionalSummary(prev.members, summaryData),
                  outgoingRequests: outgoingRequestsData,
                }));
              })
              .catch((error) => {
                console.error("Failed to refresh data after cancelling request:", error);
              });
          },
          (error) => {
            console.error("Failed to cancel request:", error);
            SwalAlert.fire({
              title: "Error!",
              text: "Failed to cancel request. Please try again.",
              icon: "error",
              button: "OK",
            });
          }
        );
      }
    });
  };

  // Handle tab change and refresh specific data
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);

    // Refresh specific data based on tab
    if (newTab === "dashboard") {
      // Refresh dashboard data (summary and members)
      Promise.all([loadSummaryData(), loadMembersData()])
        .then(([summaryData, membersData]) => {
          const transformedMembers = transformApiDataToComponentFormat(membersData, ranks);
          const summary = calculateAdditionalSummary(transformedMembers, summaryData);

          setTeamData((prev) => ({
            ...prev,
            summary,
            members: transformedMembers,
          }));
        })
        .catch((error) => {
          console.error("Failed to refresh dashboard data:", error);
        });
    } else if (newTab === "requests") {
      // Refresh requests data (incoming and outgoing)
      Promise.all([loadIncomingRequests(), loadOutgoingRequests()])
        .then(([incomingRequestsData, outgoingRequestsData]) => {
          setTeamData((prev) => ({
            ...prev,
            incomingRequests: incomingRequestsData,
            outgoingRequests: outgoingRequestsData,
          }));
        })
        .catch((error) => {
          console.error("Failed to refresh requests data:", error);
        });
    }
  };

  if (loading) {
    return (
      <PageLayout activeSideMenu="10" pageTitle="Dashboard">
        <div
          className="my-core-team-container"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Loader2 size={48} color="var(--primary)" className="animate-spin" />
            <p style={{ marginTop: "16px", color: "var(--text-secondary)" }}>Loading your core team...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Only show global error for critical issues like missing distributor info
  if (error && !distributor?.distributor_id) {
    return (
      <PageLayout activeSideMenu="10" pageTitle="My Core Team">
        <div className="my-core-team-container">
          <div className="my-core-team-alert my-core-team-warning-alert">
            <X size={20} color="var(--warning)" />
            <span>{error}</span>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout activeSideMenu="10" pageTitle="My Core Team">
      <div className="my-core-team-container">
        {/* Header */}
        <div className="my-core-team-header">
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <h1 className="my-core-team-title">My Core Team</h1>
          </div>
          <button
            className="my-core-team-button my-core-team-primary-button"
            onClick={() => setShowSearchModal(true)}
            disabled={
              teamData.summary.totalMembers >= teamData.summary.maxMembers || apiErrors.summary || apiErrors.members
            }
          >
            <Plus size={20} color="white" />
            Add Member ({apiErrors.summary ? "N/A" : teamData.summary.totalMembers}/
            {apiErrors.summary ? "N/A" : teamData.summary.maxMembers})
            {teamData.summary.totalMembers >= teamData.summary.maxMembers && " (Team Full)"}
          </button>
        </div>

        {/* Stats */}
        <div className="my-core-team-stats-grid">
          <StatCard
            title="Active Members"
            value={apiErrors.summary ? "N/A" : `${teamData.summary.activeMembers}/${teamData.summary.totalMembers}`}
            subtitle={apiErrors.summary ? "Data unavailable" : "Currently active"}
            icon={<User size={24} color={apiErrors.summary ? "var(--text-secondary)" : "var(--success)"} />}
            color={apiErrors.summary ? "var(--text-secondary)" : "var(--success)"}
          />
          <StatCard
            title="Pending Requests"
            value={apiErrors.summary ? "N/A" : teamData.summary.pendingRequests}
            subtitle={apiErrors.summary ? "Data unavailable" : "Awaiting response"}
            icon={<Calendar size={24} color={apiErrors.summary ? "var(--text-secondary)" : "var(--warning)"} />}
            color={apiErrors.summary ? "var(--text-secondary)" : "var(--warning)"}
          />
        </div>

        {/* Tabs */}
        <div className="my-core-team-tab-container">
          <button
            className={`my-core-team-tab ${activeTab === "dashboard" ? "my-core-team-active-tab" : ""}`}
            onClick={() => handleTabChange("dashboard")}
            aria-label="Team Dashboard"
            role="tab"
            aria-selected={activeTab === "dashboard"}
          >
            Team Dashboard
          </button>
          <button
            className={`my-core-team-tab ${activeTab === "requests" ? "my-core-team-active-tab" : ""}`}
            onClick={() => handleTabChange("requests")}
            aria-label="Requests"
            role="tab"
            aria-selected={activeTab === "requests"}
          >
            Requests ({teamData.incomingRequests.length + teamData.outgoingRequests.length})
          </button>
        </div>

        {/* Content */}
        {activeTab === "dashboard" && (
          <div>
            {apiErrors.members ? (
              <div className="my-core-team-alert my-core-team-warning-alert">
                <X size={20} color="var(--warning)" />
                <span>
                  Failed to load team members. Please try refreshing the page or contact support if the issue persists.
                </span>
              </div>
            ) : teamData.members.length === 0 ? (
              <div className="my-core-team-alert my-core-team-info-alert">
                <User size={20} color="var(--info)" />
                <span>You haven't added any members to your core team yet. Click "Add Member" to get started.</span>
              </div>
            ) : (
              teamData.members.map((member) => (
                <MemberCard
                  key={member.id}
                  member={member}
                  onRemove={handleRemoveMember}
                  onViewDashboard={handleViewDashboard}
                />
              ))
            )}
          </div>
        )}

        {activeTab === "requests" && (
          <div>
            {/* Incoming Requests */}
            <div style={{ marginBottom: "32px" }}>
              <h3 style={{ margin: "0 0 16px", fontSize: "20px", fontWeight: 600, color: "var(--text-primary)" }}>
                Incoming Requests ({teamData.incomingRequests.length})
              </h3>
              {apiErrors.incomingRequests ? (
                <div className="my-core-team-alert my-core-team-warning-alert">
                  <X size={20} color="var(--warning)" />
                  <span>
                    Failed to load incoming requests. Please try refreshing the page or contact support if the issue
                    persists.
                  </span>
                </div>
              ) : teamData.incomingRequests.length === 0 ? (
                <div className="my-core-team-alert my-core-team-info-alert">
                  <Calendar size={20} color="var(--info)" />
                  <span>No incoming requests at the moment.</span>
                </div>
              ) : (
                teamData.incomingRequests.map((request) => (
                  <div key={request.receiver_id} className="my-core-team-request-card">
                    <div className="my-core-team-request-content">
                      <div className="my-core-team-request-info">
                        <h4 className="my-core-team-request-name">{request.NAME}</h4>
                        <p className="my-core-team-request-details">
                          {request.rank} | ID: {request.sender_distributor_id}
                        </p>
                        <p className="my-core-team-request-status">
                          Status: {request.status} | Sent on {AppUtils.formatDate(request.created_at)}
                        </p>
                      </div>
                      <div className="my-core-team-request-actions">
                        <button
                          className="my-core-team-request-button my-core-team-request-accept-button"
                          onClick={() => handleAcceptRequest(request.sender_distributor_id)}
                        >
                          <Check size={16} color="white" />
                          Accept
                        </button>
                        <button
                          className="my-core-team-request-button my-core-team-request-decline-button"
                          onClick={() => handleDeclineRequest(request.sender_distributor_id)}
                        >
                          <X size={16} color="white" />
                          Decline
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Outgoing Requests */}
            <div>
              <h3 style={{ margin: "0 0 16px", fontSize: "20px", fontWeight: 600, color: "var(--text-primary)" }}>
                Outgoing Requests ({teamData.outgoingRequests.length})
              </h3>
              {apiErrors.outgoingRequests ? (
                <div className="my-core-team-alert my-core-team-warning-alert">
                  <X size={20} color="var(--warning)" />
                  <span>
                    Failed to load outgoing requests. Please try refreshing the page or contact support if the issue
                    persists.
                  </span>
                </div>
              ) : teamData.outgoingRequests.length === 0 ? (
                <div className="my-core-team-alert my-core-team-info-alert">
                  <Calendar size={20} color="var(--info)" />
                  <span>No outgoing requests at the moment.</span>
                </div>
              ) : (
                teamData.outgoingRequests.map((request) => (
                  <div key={request.receiver_id} className="my-core-team-request-card">
                    <div className="my-core-team-request-content">
                      <div className="my-core-team-request-info">
                        <h4 className="my-core-team-request-name">{request.NAME}</h4>
                        <p className="my-core-team-request-details">
                          {request.rank} | ID: {request.receiver_distributor_id}
                        </p>
                        <p className="my-core-team-request-status">
                          Status: {request.status} | Sent on {AppUtils.formatDate(request.created_at)}
                        </p>
                      </div>
                      <div className="my-core-team-request-actions">
                        <span className="my-core-team-chip my-core-team-warning-chip">Pending Response</span>
                        <button
                          className="my-core-team-request-button my-core-team-request-cancel-button"
                          onClick={() => handleCancelRequest(request.receiver_distributor_id)}
                        >
                          <X size={16} color="red" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Search Modal */}
        <SearchModal
          isOpen={showSearchModal}
          onClose={() => setShowSearchModal(false)}
          onAdd={handleAddMember}
          existingMembers={teamData.members}
          summary={teamData.summary}
        />
      </div>
    </PageLayout>
  );
};

export default MyCoreTeam;
